<script lang="ts" setup>
    const {
        handle,
        fieldLabels = true,
        submitArrow = false,
        submitLabel = 'Send',
        blendedInputs = false,
        submitCallback = undefined,
        loadingOverride = undefined,
    } = defineProps<{
        handle: string
        fieldLabels?: boolean
        submitArrow?: boolean
        submitLabel?: string
        blendedInputs?: boolean
        submitCallback?: any
        loadingOverride?: boolean
    }>()

    const formEl = ref()
    const { data: form } = await useStatamicForms().getForm({ handle })
    const data: Record<string, any> = ref({})
    const loading = ref(false)
    const success: Ref<boolean | null> = ref(null)

    const isLoading = computed(() => loading.value || loadingOverride)

    const setDefaults = () => {
        for (const [name, field] of Object.entries(form.value.fields)) {
            data.value[name] = field.default ?? ''
        }

        data.value.project_type = null
        data.value.project_category = null
    }

    const submit = async () => {
        if (submitCallback) {
            submitCallback(data.value)
            return
        }

        loading.value = true

        useStatamicForms()
            .submit({ handle, data: data.value })
            .then(() => {
                setDefaults()
                loading.value = false
                success.value = true

                setTimeout(() => {
                    success.value = null
                }, 7000)
            })
            .catch(() => {
                loading.value = false
                success.value = false

                setTimeout(() => {
                    success.value = null
                }, 3000)
            })
    }
</script>

<template>
    <form
        ref="formEl"
        @submit.prevent="submit"
        :class="{ 'blended-inputs': blendedInputs }"
        class="relative"
        :data-handle="handle"
    >
        <Transition>
            <div
                v-if="success"
                class="form-success absolute inset-0 z-10 bg-light/90"
            >
                <div class="middle border-2 bg-light px-6 py-2 text-center">
                    {{ form.success_message || 'Thanks for your submission!' }}
                </div>
            </div>
        </Transition>

        <Transition>
            <div
                v-if="success === false"
                class="form-error absolute inset-0 z-10 bg-light/90"
            >
                <div class="middle border-2 bg-light px-6 py-2">
                    {{
                        form.error_message ||
                        'Error submitting form. Please try again.'
                    }}
                </div>
            </div>
        </Transition>

        <InputFields
            :fields="form.fields"
            :field-labels="fieldLabels"
            v-model="data"
        />

        <div class="button-wrapper">
            <PrettyButton
                type="submit"
                :class="{
                    'icon-arrow absolute bottom-0 right-4 z-10 mt-10 w-[18px] border-0 after:bg-medium after:transition hover:!bg-transparent hover:after:translate-x-1/2 hover:after:bg-white':
                        submitArrow,
                    'after:opacity-0': submitArrow && isLoading,
                    'mt-10 w-full': !submitArrow,
                }"
                :loading="isLoading"
            >
                <template v-if="!submitArrow">{{ submitLabel }}</template>
            </PrettyButton>
        </div>
    </form>
</template>

<style>
    form.blended-inputs {
        input {
            @apply pl-0;
            @apply bg-transparent;
            @apply border-0 border-b border-b-medium;
            @apply placeholder:text-[#ffffff60];
        }
    }
</style>
